import { IonModal, IonText } from "@ionic/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { usePostHog } from "posthog-js/react";
import { z } from "zod";
import { CoreValueAvatarWithBackground } from "@/components/CoreValue/AvatarWithBackground";
import { PersonalizeStatusCard } from "@/components/CoreValue/PersonalizeStatusCard";
import { Button } from "@/components/Global/Button";
import { Content } from "@/components/Global/Content";
import { LoadingText } from "@/components/Loading/LoadingText";
import { backendService } from "@/lib/backend";
import { queryOptions_coreValuesChosen } from "@/lib/query/functions/core-values/chosen";
import { useMyData } from "@/lib/query/functions/user/data";
import { useMutationStream } from "@/lib/query/hooks/useMutationStream";
import { useSelectorModalStore } from "@/lib/store/modal";
import { jsonParse } from "@/utils/json/parse";

export function ModalCoreValuesResummarize() {
	const { t } = useTranslation();
	const posthog = usePostHog();
	const resummarizeModalRef = useRef<HTMLIonModalElement>(null);
	const modalRef = useRef<HTMLIonModalElement>(null);
	const modal = useSelectorModalStore.use.coreValuesResummarize();
	const present = useSelectorModalStore.use.present();
	const dismiss = useSelectorModalStore.use.dismiss();
	const [, { mutation: mutationMyData }] = useMyData();

	const queryCoreValuesChosen = useQuery({
		...queryOptions_coreValuesChosen(),
		enabled: modal.isOpen,
	});

	const [summary, querySummaryStream] = useMutationStream({
		mutationKey: ["core-values", "summary-stream"],
		mutationFn: (params) =>
			backendService.corevalues.coreValuesControllerGenerateCoreValueSummaryStream(
				{
					type: "corevalues",
				},
				params,
			),
		onSuccess: () => {
			mutationMyData.mutate((draft) => void (draft.coreValues.hasRegeneratedSummary = true));
		},
	});

	const parsedSummary = jsonParse({
		input: summary || "",
		schema: z.object({ summary: z.string().nullish() }),
	});

	return (
		<>
			<IonModal
				ref={modalRef}
				isOpen={modal.isOpen}
				onWillPresent={() => {
					present("coreValuesResummarize");
					posthog.capture("Modal CoreValues Resummarize Present");
				}}
				onWillDismiss={() => {
					dismiss("coreValuesResummarize");
					posthog.capture("Modal CoreValues Resummarize Dismiss");
				}}
				onDidDismiss={() => {
					resummarizeModalRef.current?.present();
				}}
				initialBreakpoint={1}
				breakpoints={[0, 1]}
				className="ion-h-auto ion-rounded-4xl"
			>
				<div className="ion-no-border flex w-full flex-col gap-4 p-4">
					<div className="flex flex-col gap-2">
						<IonText className="text-brown-700 text-2xl font-bold">
							{t("modal.core-values.regenerate.explainer.title")}
						</IonText>
						<IonText className="text-brown-600">
							{t("modal.core-values.regenerate.explainer.description")}
						</IonText>
					</div>

					<PersonalizeStatusCard coreValues={queryCoreValuesChosen.data ?? []} />

					<Button
						onClick={() => {
							modalRef.current?.dismiss();
						}}
						className="w-full"
					>
						{t("modal.core-values.regenerate.explainer.action")}
					</Button>
				</div>
			</IonModal>

			<IonModal
				ref={resummarizeModalRef}
				onDidPresent={() => {
					querySummaryStream.mutate();
				}}
				onDidDismiss={() => {
					present("coreValuesFinished");
				}}
				backdropDismiss={false}
				className="ion-rounded-4xl"
			>
				<Content fullscreen inModal>
					<div className="ion-no-border flex w-full flex-col gap-4">
						<CoreValueAvatarWithBackground className="my-8" />

						<div className="bg-brown-100 flex flex-col gap-2 rounded-md p-4">
							<IonText className="text-brown-500 font-semibold">{t("about-you")}</IonText>
							<IonText className="text-brown-700 font-semibold">
								{querySummaryStream.isError ? (
									<div className="text-red-500">{t("core-values.regenerate.error")}</div>
								) : parsedSummary ? (
									<motion.div transition={{ staggerChildren: 0.1 }}>
										{parsedSummary?.summary?.split(" ").map((word, index) => (
											<motion.span
												key={word + index}
												initial={{ opacity: 0 }}
												animate={{ opacity: 1 }}
												transition={{ duration: 1 }}
											>
												{word}{" "}
											</motion.span>
										))}
									</motion.div>
								) : (
									<LoadingText />
								)}
							</IonText>
						</div>

						<Button
							data-attr="modal.core-values.regenerate.regenerate.next"
							expand="block"
							className="data-[ready=false]:opacity-75"
							disabled={querySummaryStream.isPending || !parsedSummary}
							onClick={() => {
								resummarizeModalRef.current?.dismiss();
							}}
						>
							{t("modal.core-values.regenerate.regenerate.next")}
						</Button>
					</div>
				</Content>
			</IonModal>
		</>
	);
}
